import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { components } from "@contracts/generated/editor-service";

import { useApiClient } from "./ApiContext";
import { ApiError, unwrap } from "./client";
import { invalidateProjectHistory, queryKeys } from "./hooks";

/**
 * The clinician half of the publication lane (#9).
 *
 * Aliases into the generated contract, like `hooks.ts` and `preview.ts`: change
 * the contract, regenerate, and these names either still resolve or fail to
 * compile.
 *
 * `ClinicianPublicationStatus` is the whole vocabulary this surface may speak.
 * #267 closed it at four states on purpose — "a projection that could say more
 * would eventually be asked to" — and no raw GitHub coordinate is representable
 * on it, which is what makes #9's "raw candidate/PR/SHA/check vocabulary is
 * absent from the primary clinician status" a property of the schema rather
 * than of the components below. #788's deliberate Copy PR link action reads a
 * `PublicationRecord` from the listing below; it does not widen this compact
 * status projection or teach the browser to construct a GitHub coordinate.
 */
export type ClinicianPublicationStatus =
  components["schemas"]["ClinicianPublicationStatus"];
export type ClinicianPublicationState =
  components["schemas"]["ClinicianPublicationState"];
export type PublicationAttentionReason =
  components["schemas"]["PublicationAttentionReason"];
export type LifecycleClassification =
  components["schemas"]["LifecycleClassification"];
export type CreateSignoffRequest =
  components["schemas"]["CreateSignoffRequest"];
export type SignoffRecord = components["schemas"]["SignoffRecord"];
export type PublicationRecord = components["schemas"]["PublicationRecord"];
export type PublicationList = components["schemas"]["PublicationList"];
export type CandidateIdentity = components["schemas"]["CandidateIdentity"];
/**
 * Verified manager-approval evidence for ONE publication (#444).
 *
 * The second axis of the publication lane, and deliberately not part of
 * `ClinicianPublicationStatus`: that projection is closed at four lifecycle
 * states, and #267's reason for closing it — "a projection that could say more
 * would eventually be asked to" — applies equally here. Approval is a fact the
 * service OBSERVED in GitHub, published on its own resource with its own
 * freshness, and the browser renders `state` rather than composing a verdict of
 * its own.
 */
export type ApprovalEvidence = components["schemas"]["ApprovalEvidence"];
export type ApprovalEvidenceState =
  components["schemas"]["ApprovalEvidenceState"];
export type CreatePromotionRequest =
  components["schemas"]["CreatePromotionRequest"];
export type PromotionHandoffRecord =
  components["schemas"]["PromotionHandoffRecord"];
export type PromotionStatus = components["schemas"]["PromotionStatus"];
export type PromotionStatusList =
  components["schemas"]["PromotionStatusList"];

/**
 * Why an acceptance did not record.
 *
 * Kinds only; the sentences live with the surface that renders them
 * (`SIGNOFF_FAILURE_COPY` in `ui/publication/PublicationLane.tsx`), keyed by
 * these members with `satisfies`, so a new kind fails the build rather than
 * rendering blank. Same split, for the same reason, as `FreezeFailure` in
 * `api/preview.ts`.
 *
 * The two 409s are the whole point of classifying at all. They share a status
 * and mean opposite things about what the clinician should do next:
 *
 *  - `not_evidence_eligible` is about the CANDIDATE and is terminal for it. The
 *    candidate was frozen under a guard catalog nobody has ratified, its
 *    eligibility was stored at freeze and is never upgraded in place, so no
 *    amount of retrying or re-previewing this candidate will ever make it
 *    acceptable. Only a new freeze under a ratified catalog can (#269).
 *  - `no_preview_evidence` is about the PREVIEW and is recoverable right now:
 *    preview this candidate against the Project's current target and accept
 *    again. The service deliberately does not say which coordinate was wrong —
 *    "the second refusal names the recovery and deliberately not the coordinate
 *    that failed" — so neither does the copy.
 *
 * Collapsing them into one "could not accept" would tell a clinician to retry
 * something that cannot succeed, or to re-freeze when a preview was all that
 * was missing.
 */
export type SignoffFailure =
  /** 409 `candidate_not_evidence_eligible` — preview-only candidate; terminal. */
  | { kind: "not_evidence_eligible" }
  /** 409 — no qualifying preview for this exact tuple against the current target. */
  | { kind: "no_preview_evidence" }
  /**
   * 409 `stale_base` — the preview target runs a different baseline than this Project started from
   * (ADR 0008 addendum, section 4). Recoverable by the Project's baseline refresh, then a review.
   */
  | { kind: "stale_base" }
  /** 404/403/401 — outside this caller's scope, or the tuple names no candidate. */
  | { kind: "denied" }
  /** 501 — not built in this environment. Terminal, no retry. */
  | { kind: "unavailable" }
  /** Anything else, carrying the machine-readable code and the request id only. */
  | { kind: "error"; code: string; requestId: string | undefined };

export function classifySignoffFailure(error: unknown): SignoffFailure {
  if (error instanceof ApiError) {
    if (error.isForbidden || error.isUnauthenticated || error.status === 404) {
      // 404 is the service's deliberate denial for a project outside scope AND
      // for a tuple naming no stored candidate — "no oracle distinguishing
      // wrong-tuple from no-candidate". Rendering them as one thing here is
      // agreeing with that, not losing information.
      return { kind: "denied" };
    }
    if (error.isNotImplemented) {
      return { kind: "unavailable" };
    }
    if (error.code === "stale_base") {
      return { kind: "stale_base" };
    }
    if (error.isConflict) {
      return error.code === "candidate_not_evidence_eligible"
        ? { kind: "not_evidence_eligible" }
        : { kind: "no_preview_evidence" };
    }
    return { kind: "error", code: error.code, requestId: error.requestId };
  }
  return { kind: "error", code: "upstream_unavailable", requestId: undefined };
}

/**
 * Records clinician acceptance of one exact candidate (#20).
 *
 * The body is the #16 tuple and nothing else — `CreateSignoffRequest` is
 * `additionalProperties: false` with `candidate` its only member — so the TYPE
 * is what enforces that no note, no target and no free text rides along with an
 * acceptance.
 *
 * No idempotency key exists on this route, and none is invented here. The
 * contract does not offer one, and a client-minted key the service would refuse
 * as an unknown field is worse than none at all: a repeat acceptance of the same
 * tuple is the service's question to answer, not this module's to pre-empt.
 */
export function useCreateSignoffMutation(
  projectId: string,
): UseMutationResult<SignoffRecord, unknown, CreateSignoffRequest> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    async mutationFn(body: CreateSignoffRequest) {
      return unwrap(
        await client.POST("/v1/projects/{projectId}/signoffs", {
          params: { path: { projectId } },
          body,
        }),
      );
    },
    onSuccess() {
      // Acceptance moves `ProjectOverview.publicationStatus` from `none` to
      // `accepted` while the git lifecycle stays `unstarted` — #267 made that
      // the one status movement the lifecycle's own revision cannot see. The
      // same successful write appends History's candidate-accepted row, so both
      // projections must be invalidated explicitly rather than left stale.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectOverview(projectId),
      });
      invalidateProjectHistory(queryClient, projectId);
    },
    // Same rule as every other non-idempotent write in this application: an
    // automatic retry of a write with no key is how one action becomes two.
    retry: false,
  });
}

/**
 * Why a publication did not start.
 *
 * Publication runs as the environment's GitHub App installation, so no
 * GitHub-account refusal can reach the clinician any more: there is no
 * connection to establish, no token to refresh, no "reconnect". What can
 * still fail on the GitHub side is the SERVICE's own publishing identity —
 * unconfigured, refused a token, rejected by GitHub — and that is an
 * administrator's condition, stated to the clinician as such with the one
 * sentence that matters: the accepted version is safe.
 */
export type PublicationFailure =
  /** 409 `candidate_not_evidence_eligible` — preview-only candidate; terminal. */
  | { kind: "not_evidence_eligible" }
  /** 409 `head_sha_mismatch` — the branch moved under a re-publish. */
  | { kind: "head_moved" }
  /** 409 `idempotency_conflict` — this key already published a different body. */
  | { kind: "idempotency_conflict" }
  /**
   * 503 `publishing_authority_unavailable` — the environment's GitHub App
   * publishing identity is not configured or GitHub refused to issue or
   * accept its token — or 503 `upstream_unavailable` (GitHub unreachable).
   * Transient from the clinician's seat: nothing about the accepted candidate
   * changed, a retry replays the same idempotency key, and an administrator
   * owns the fix if it persists.
   */
  | { kind: "publishing_unavailable"; requestId: string | undefined }
  /**
   * 403 `github_permission_denied` — GitHub definitively refused the
   * publishing service's request (a rule refusing the App, a drifted
   * installation). Not the clinician's authorization and not repairable from
   * here; an administrator checks the publishing service's access.
   */
  | { kind: "publishing_refused"; requestId: string | undefined }
  /** 401 / 403 `forbidden` / 404 — the caller's own session or Editor role. */
  | { kind: "denied" }
  /** 501 — not built in this environment. Terminal, no retry. */
  | { kind: "unavailable" }
  | { kind: "error"; code: string; requestId: string | undefined };

export function classifyPublicationFailure(error: unknown): PublicationFailure {
  if (error instanceof ApiError) {
    if (error.code === "publishing_authority_unavailable") {
      return { kind: "publishing_unavailable", requestId: error.requestId };
    }
    if (error.code === "github_permission_denied") {
      return { kind: "publishing_refused", requestId: error.requestId };
    }
    if (error.isForbidden || error.isUnauthenticated || error.status === 404) {
      return { kind: "denied" };
    }
    if (error.isNotImplemented) {
      return { kind: "unavailable" };
    }
    if (error.isConflict) {
      if (error.code === "candidate_not_evidence_eligible") {
        return { kind: "not_evidence_eligible" };
      }
      if (error.code === "head_sha_mismatch") {
        return { kind: "head_moved" };
      }
      if (error.code === "idempotency_conflict") {
        return { kind: "idempotency_conflict" };
      }
    }
    if (error.status === 503) {
      return { kind: "publishing_unavailable", requestId: error.requestId };
    }
    return { kind: "error", code: error.code, requestId: error.requestId };
  }
  return { kind: "error", code: "upstream_unavailable", requestId: undefined };
}

/**
 * Publishes an accepted candidate as a requester-attributed branch and draft
 * overlay PR, opened by the environment's GitHub App (#48, App identity).
 *
 * The request body is built by `publishing/publicationRequest.ts`, not here:
 * the idempotency key is minted at the first request for a candidate and kept
 * for its retries, so a replay after a transient failure returns the original
 * record instead of a second pull request. This hook is the POST and nothing
 * else — the caller's session and Editor role are the whole precondition.
 */
export function useCreatePublicationMutation(
  projectId: string,
): UseMutationResult<
  PublicationRecord,
  unknown,
  components["schemas"]["CreatePublicationRequest"]
> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    async mutationFn(body) {
      return unwrap(
        await client.POST("/v1/projects/{projectId}/publications", {
          params: { path: { projectId } },
          body,
        }),
      );
    },
    onSuccess() {
      // A publication starts the git lifecycle, which the compact status reads
      // through: `unstarted` → a live rung, i.e. `accepted` → the honest
      // `publication_requested`. Both the overview and the promotion listing
      // beside it are now stale.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectOverview(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectPromotions(projectId),
      });
      // And the listing this request just added a row to, which is what makes
      // a `publicationId` reachable at all (#445). History is the fourth read:
      // the request itself is one of its immutable event sources.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectPublications(projectId),
      });
      invalidateProjectHistory(queryClient, projectId);
    },
    retry: false,
  });
}

/**
 * The project's publications, newest first.
 *
 * The ONLY way a `publicationId` reaches the browser. `ClinicianPublicationStatus`
 * deliberately carries none — it is a compact lifecycle reading, not a handle —
 * so a surface that needs to ask about one publication has to select it from
 * here first.
 *
 * NOTHING POLLS. `hooks.ts` records the convention and its reason, and approval
 * state changes externally in GitHub rather than in response to anything this
 * application did — which is an argument for an explicit refresh affordance, not
 * for a background interval that would run in every idle tab. Refetch-on-mount
 * under the client's own defaults plus that affordance is the whole freshness
 * story here.
 *
 * `enabled` is the caller's, because the caller knows the lifecycle state: a
 * Project that has never requested publication has no row to list, and asking
 * anyway would put a request on the critical path of every Overview mount.
 */
export function usePublicationsQuery(
  projectId: string,
  { enabled = true }: { enabled?: boolean } = {},
): UseQueryResult<PublicationList> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.projectPublications(projectId),
    async queryFn() {
      return unwrap(
        await client.GET("/v1/projects/{projectId}/publications", {
          params: { path: { projectId } },
        }),
      );
    },
    enabled: enabled && projectId.length > 0,
  });
}

/**
 * The Project's current publication, by the service's own rule.
 *
 * Newest non-failed row, falling back to the newest failed one — the same
 * ordering `current_publication_statement` applies server-side, over a listing
 * the service already returned in `createdAt DESC, publicationId DESC` order. A
 * pure selection over rows that arrived ranked, and no more: this exists so a
 * publication can be NAMED, not so the browser can adjudicate currency.
 *
 * WHICH IS A DIFFERENT QUESTION FROM WHETHER IT IS STILL CURRENT, and only the
 * service answers that one. `ApprovalEvidenceState.superseded` is its verdict —
 * computed against the row it locked, not against a listing that may be seconds
 * old — and that verdict is what the surface renders. So a selection that lost a
 * race reads "replaced" rather than reviving a retired publication, which is the
 * fail-closed direction (#437 rule 4).
 *
 * Deliberately unfiltered by lane. The service's own statement does not filter
 * either, and a #204 trial publication that IS the newest row is answered with
 * `unavailable` — "carries no manager approval by design" — which is the honest
 * reading. Hiding it here would make the surface silent instead.
 */
export function currentPublicationOf(
  list: PublicationList,
): PublicationRecord | null {
  return (
    list.items.find((publication) => publication.state !== "failed") ??
    list.items[0] ??
    null
  );
}

/**
 * The promotion handoff already on record for one candidate, if any.
 *
 * This is what replaces a local "sent" flag. The handoff is an immutable row and
 * the service ADOPTS an existing one for the same `(candidate, approval)` pair,
 * keeping its original note and document — so a surface that offered the form
 * again would be collecting evidence the service is about to discard while
 * reporting it as recorded. The record itself therefore has to be the authority,
 * and `GET .../promotions` is where it can be read after a reload as well as
 * after a press.
 *
 * MATCHED ON THE CANDIDATE TUPLE, because that is the finest identity this
 * listing carries: `PromotionStatus` is `{candidate, requestState,
 * executionState, requestedAt}` and exposes neither `handoffId` — so
 * `GET .../promotions/{handoffId}` cannot be reached from here — nor
 * `approvalId`. The whole tuple is compared rather than the id alone, for the
 * same reason the lane compares it: an identifier that matched while a hash did
 * not would be a different version wearing the same name.
 *
 * The consequence is stated rather than hidden. A candidate that already has a
 * handoff bound to an OLDER approval, where a newer approval would genuinely be
 * accepted, reads here as already sent and withholds the action. That is the
 * fail-closed direction — it can only refuse a mint the service would have
 * allowed, never claim evidence that was discarded — and closing it exactly needs
 * an `approvalId` on this listing, which is a contract change and not this
 * ticket's.
 */
export function handoffOnRecordFor(
  list: PromotionStatusList,
  candidate: CandidateIdentity,
): PromotionStatus | null {
  return (
    list.items.find(
      (promotion) =>
        promotion.candidate.candidateId === candidate.candidateId &&
        promotion.candidate.contentHash === candidate.contentHash &&
        promotion.candidate.basePackSha === candidate.basePackSha,
    ) ?? null
  );
}

/**
 * Verified manager-approval evidence for one publication (#444).
 *
 * Held to the publication it was read for by the query key, so evidence about a
 * retired publication can never be served for a newer one out of the cache.
 */
export function useApprovalEvidenceQuery(
  projectId: string,
  publicationId: string | null,
): UseQueryResult<ApprovalEvidence> {
  const client = useApiClient();
  return useQuery({
    queryKey: queryKeys.approvalEvidence(projectId, publicationId ?? ""),
    async queryFn() {
      return unwrap(
        await client.GET(
          "/v1/projects/{projectId}/publications/{publicationId}/approval-evidence",
          {
            params: {
              path: { projectId, publicationId: publicationId as string },
            },
          },
        ),
      );
    },
    enabled: projectId.length > 0 && publicationId !== null,
  });
}

/**
 * Why approval progress could not be read.
 *
 * Kinds only, sentences with the surface, exactly as every other classifier in
 * this module. Narrower than `SectionFailure` because this route has fewer ways
 * to refuse: it composes stored observations and reaches no engine.
 */
export type ApprovalEvidenceFailure =
  /** 404/403/401 — outside this caller's scope, or no such publication. */
  | { kind: "denied" }
  /** 501 — not built in this environment. Terminal, no retry. */
  | { kind: "unavailable" }
  /** Anything else, carrying the machine-readable code and the request id only. */
  | { kind: "error"; code: string; requestId: string | undefined };

export function classifyApprovalEvidenceFailure(
  error: unknown,
): ApprovalEvidenceFailure {
  if (error instanceof ApiError) {
    if (error.isForbidden || error.isUnauthenticated || error.status === 404) {
      return { kind: "denied" };
    }
    if (error.isNotImplemented) {
      return { kind: "unavailable" };
    }
    return { kind: "error", code: error.code, requestId: error.requestId };
  }
  return { kind: "error", code: "upstream_unavailable", requestId: undefined };
}

/**
 * Why a promotion handoff did not mint.
 *
 * Three families, and keeping them apart is the whole reason to classify:
 *
 *  - THE DOCUMENT was refused. `evidence_not_pdf`, `evidence_too_large` and
 *    `evidence_corrupt` are the service's three named refusals, and each has a
 *    different recovery — pick a different file, pick a smaller one, send the
 *    same one again. The browser pre-checks the same three to fail fast, and
 *    returns these same members so one copy map serves both sides; the SERVER
 *    stays the authority, because it judges the bytes rather than a declared
 *    part type and this surface never re-decides what it refused.
 *  - THE WORLD MOVED. `not_merged`, `approval_not_bindable` and
 *    `candidate_mismatch` all mean the handoff's preconditions no longer hold.
 *    None is retryable by pressing the button again, and `approval_not_bindable`
 *    in particular must NOT read as a transient error: the contract calls the
 *    approval "caller-named", so a refusal means the named approval is unknown,
 *    not approved, invalidated, or covers a different head. That is a fail-closed
 *    refusal, and the recovery is a fresh reading of the evidence.
 *  - THE ATTEMPT COLLIDED. `idempotency_conflict` is its own member because the
 *    fingerprint covers the DOCUMENT's digest: the same key with the same bytes
 *    replays the original handoff and answers 201, so reaching this code means
 *    the note or the document changed under a key that was already spent. A
 *    generic "try again" would loop forever on it.
 */
export type PromotionFailure =
  /** 422 `manager_evidence_unsupported_media_type` — the bytes are not a PDF. */
  | { kind: "evidence_not_pdf" }
  /** 422 `manager_evidence_too_large`, or a 413 from the ingress. */
  | { kind: "evidence_too_large" }
  /** 422 `manager_evidence_corrupt` — empty, or truncated before its trailer. */
  | { kind: "evidence_corrupt" }
  /** 422 `validation_failed` — a missing or malformed field, `note` included. */
  | { kind: "invalid" }
  /** 409 `idempotency_conflict` — this key already sent a different body. */
  | { kind: "idempotency_conflict" }
  /** 409 `approval_not_bindable` — fail-closed; the named approval cannot bind. */
  | { kind: "approval_not_bindable" }
  /** 409 `publication_not_merged` — the recovery is waiting, never editing. */
  | { kind: "not_merged" }
  /** 409 `candidate_not_evidence_eligible` — preview-only candidate; terminal. */
  | { kind: "not_evidence_eligible" }
  /** 409 `conflict` — the bound tuple disagrees with the stored candidate. */
  | { kind: "candidate_mismatch" }
  | { kind: "denied" }
  /** 501 — not built in this environment. Terminal, no retry. */
  | { kind: "unavailable" }
  | { kind: "error"; code: string; requestId: string | undefined };

/**
 * The refusals that mean the preconditions moved rather than the input was bad.
 *
 * Exported because the surface has to ACT on the distinction — a moved
 * precondition invalidates the evidence read that offered the action, so the
 * action withdraws itself without a reload (#445 criterion 5) — and deriving it
 * at each call site is how two lists drift apart.
 */
export function isMovedPrecondition(failure: PromotionFailure): boolean {
  return (
    failure.kind === "approval_not_bindable" ||
    failure.kind === "not_merged" ||
    failure.kind === "candidate_mismatch" ||
    failure.kind === "not_evidence_eligible"
  );
}

export function classifyPromotionFailure(error: unknown): PromotionFailure {
  if (error instanceof ApiError) {
    if (error.isForbidden || error.isUnauthenticated || error.status === 404) {
      return { kind: "denied" };
    }
    if (error.isNotImplemented) {
      return { kind: "unavailable" };
    }
    // Ahead of the 422 arm: an ingress that caps the body answers 413 with no
    // envelope of its own, and the clinician's recovery is the same as the
    // service's own size refusal — a smaller document.
    if (error.status === 413) {
      return { kind: "evidence_too_large" };
    }
    if (error.status === 422) {
      if (error.code === "manager_evidence_unsupported_media_type") {
        return { kind: "evidence_not_pdf" };
      }
      if (error.code === "manager_evidence_too_large") {
        return { kind: "evidence_too_large" };
      }
      if (error.code === "manager_evidence_corrupt") {
        return { kind: "evidence_corrupt" };
      }
      return { kind: "invalid" };
    }
    if (error.isConflict) {
      if (error.code === "idempotency_conflict") {
        return { kind: "idempotency_conflict" };
      }
      if (error.code === "approval_not_bindable") {
        return { kind: "approval_not_bindable" };
      }
      if (error.code === "publication_not_merged") {
        return { kind: "not_merged" };
      }
      if (error.code === "candidate_not_evidence_eligible") {
        return { kind: "not_evidence_eligible" };
      }
      // `conflict` is both the service's identity-tuple refusal and what
      // `CODE_BY_STATUS` falls back to for a 409 carrying no envelope. Both
      // mean the same thing to a clinician: what was bound is not what is
      // stored, and no retry of this body changes that.
      return { kind: "candidate_mismatch" };
    }
    return { kind: "error", code: error.code, requestId: error.requestId };
  }
  return { kind: "error", code: "upstream_unavailable", requestId: undefined };
}

/** What the post-merge handoff binds, before it becomes a multipart body. */
export interface PromotionHandoffInput {
  /** The publication's own candidate, from the evidence — never the newest. */
  candidate: CandidateIdentity;
  /**
   * The exact approval the evidence reported, and nothing the browser chose.
   *
   * "Two live approvals are two true facts; the server never guesses 'newest'",
   * so a caller that picked one would be asserting an approval fact. This is
   * carried through from `ApprovalEvidence.approvalId` verbatim.
   */
  approvalId: string;
  /** The clinician's justification. Mandatory since #358; 1–500 characters. */
  note: string;
  /**
   * Client-minted at intent, and STABLE across a retry of the same attempt.
   *
   * Minting a fresh key per press would defeat the guard exactly when it
   * matters. The fingerprint covers the document's content digest too, so the
   * same key with the same bytes replays the original handoff and a changed
   * note or document answers `idempotency_conflict` — which is a distinct,
   * actionable state rather than an error to retry.
   */
  idempotencyKey: string;
  /** The signed approval document, as the `managerEvidence` multipart part. */
  managerEvidence: File;
}

/**
 * Every declared field as one multipart part, the document included.
 *
 * openapi-fetch sets no `Content-Type` when the serialized body is `FormData`,
 * which is required rather than convenient: only the browser can append the
 * multipart boundary, and a hand-written header would produce a body the
 * service cannot parse.
 */
function multipartBody(body: CreatePromotionRequest): FormData {
  const form = new FormData();
  for (const [name, value] of Object.entries(body)) {
    form.append(name, value as string | File);
  }
  return form;
}

/**
 * Mints the post-merge handoff: the clinician's note and the signed approval
 * document, bound to the exact candidate and approval the evidence reported
 * (#358, #50).
 *
 * ONE REQUEST, because the contract is one request. #358 chose
 * `multipart/form-data` so that "validate media type, size, empty/corrupt input
 * and storage failure BEFORE creating the request" is the literal order of
 * operations, and an unbound upload is structurally impossible rather than
 * merely cleaned up afterwards. An upload-then-bind pair here would give that
 * property away from the client side.
 *
 * The candidate tuple is flattened because form encoding cannot express a
 * nested object. It is still bound WHOLE: `satisfies CreatePromotionRequest`
 * below checks every field name and type against the generated schema, so a
 * contract rename fails the build rather than silently dropping a part.
 */
export function useRequestPromotionMutation(
  projectId: string,
): UseMutationResult<PromotionHandoffRecord, unknown, PromotionHandoffInput> {
  const client = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    async mutationFn(input: PromotionHandoffInput) {
      const body = {
        candidateId: input.candidate.candidateId,
        contentHash: input.candidate.contentHash,
        basePackSha: input.candidate.basePackSha,
        approvalId: input.approvalId,
        note: input.note,
        idempotencyKey: input.idempotencyKey,
        // The ONE field the generated type cannot describe: openapi-typescript
        // renders `format: binary` as `string`, and the wire wants the file
        // itself. `multipartBody` is what puts it there, and this cast is
        // confined to the single field rather than to the whole body — every
        // other name above is still checked against the contract.
        managerEvidence: input.managerEvidence as unknown as string,
      } satisfies CreatePromotionRequest;

      return unwrap(
        await client.POST("/v1/projects/{projectId}/promotions", {
          params: { path: { projectId } },
          body,
          bodySerializer: multipartBody,
        }),
      );
    },
    onSuccess() {
      // A minted handoff moves the promotion listing the read-only Promotion
      // card renders, and the publication's own evidence — `bindable` is false
      // for an approval already spent — so both are stale the moment this
      // returns. The overview goes with them: the handoff is the rung after
      // publication and the lifecycle reading is derived from the same rows.
      // The minted handoff is also an immutable History event.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectPromotions(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectPublications(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.projectOverview(projectId),
      });
      invalidateProjectHistory(queryClient, projectId);
    },
    // Same rule as every other non-idempotent write here. The key would make a
    // replay safe, but an automatic retry would also replay a refusal the
    // clinician has not seen yet.
    retry: false,
  });
}
